import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import UFOSegment from "@atlaskit/react-ufo/segment";

import Card, { CardProps } from "@/components/wpl/card/src";
import { Link } from "@/libraries/design/components";
import { useLocalizedHref } from "@/libraries/web-framework";
import { Row } from "@/libraries/wpl/foundations/src";

export const PlatformSection = () => {
    const intl = useIntl();

    const platformCardsCrossProductExperience: CardProps[] = [
        {
            headerText: intl.formatMessage({
                id: "EnterpriseLandingPage.Platform.crossProductExperince.card.collaboration.headerText",
                defaultMessage: "Collaboration"
            }),
            bodyText: intl.formatMessage({
                id: "EnterpriseLandingPage.Platform.crossProductExperince.card.collaboration.bodyText",
                defaultMessage: "Keep work flowing across teams and tools."
            }),
            ctaProps: {
                primaryLinkProps: {
                    text: intl.formatMessage({
                        id: "EnterpriseLandingPage.Platform.crossProductExperince.card.linkText",
                        defaultMessage: "Learn more"
                    }),
                    href: useLocalizedHref("/platform/collaboration"),
                    type: "primary",
                    dataEventProps: {
                        "data-product-key": "enterprise",
                        "data-event-component": "linkButton",
                        "data-event-container": "platformSection",
                        "data-event": "clicked",
                        "data-label": intl.formatMessage({
                            id: "EnterpriseLandingPage.Platform.crossProductExperince.card.linkText",
                            defaultMessage: "Learn more"
                        }),
                        "data-label-english": "Learn more"
                    }
                }
            }
        },
        {
            headerText: intl.formatMessage({
                id: "EnterpriseLandingPage.Platform.crossProductExperince.card.intelligence.headerText",
                defaultMessage: "Intelligence"
            }),
            bodyText: intl.formatMessage({
                id: "EnterpriseLandingPage.Platform.crossProductExperince.card.intelligence.bodyText",
                defaultMessage:
                    "Transform teamwork with the power of AI-human collaboration."
            }),
            ctaProps: {
                primaryLinkProps: {
                    text: intl.formatMessage({
                        id: "EnterpriseLandingPage.Platform.crossProductExperince.card.linkText",
                        defaultMessage: "Learn more"
                    }),
                    href: useLocalizedHref("/software/artificial-intelligence"),
                    type: "primary",
                    dataEventProps: {
                        "data-product-key": "enterprise",
                        "data-event-component": "linkButton",
                        "data-event-container": "platformSection",
                        "data-event": "clicked",
                        "data-label": intl.formatMessage({
                            id: "EnterpriseLandingPage.Platform.crossProductExperince.card.linkText",
                            defaultMessage: "Learn more"
                        }),
                        "data-label-english": "Learn more"
                    }
                }
            }
        },
        {
            headerText: intl.formatMessage({
                id: "EnterpriseLandingPage.Platform.crossProductExperince.card.automation.headerText",
                defaultMessage: "Automation"
            }),
            bodyText: intl.formatMessage({
                id: "EnterpriseLandingPage.Platform.crossProductExperince.card.automation.bodyText",
                defaultMessage:
                    "Replace manual, time-consuming work with modern workflows."
            }),
            ctaProps: {
                primaryLinkProps: {
                    text: intl.formatMessage({
                        id: "EnterpriseLandingPage.Platform.crossProductExperince.card.linkText",
                        defaultMessage: "Learn more"
                    }),
                    href: useLocalizedHref("/platform/automation"),
                    type: "primary",
                    dataEventProps: {
                        "data-product-key": "enterprise",
                        "data-event-component": "linkButton",
                        "data-event-container": "platformSection",
                        "data-event": "clicked",
                        "data-label": intl.formatMessage({
                            id: "EnterpriseLandingPage.Platform.crossProductExperince.card.linkText",
                            defaultMessage: "Learn more"
                        }),
                        "data-label-english": "Learn more"
                    }
                }
            }
        },
        {
            headerText: intl.formatMessage({
                id: "EnterpriseLandingPage.Platform.crossProductExperince.card.analytics.headerText",
                defaultMessage: "Analytics"
            }),
            bodyText: intl.formatMessage({
                id: "EnterpriseLandingPage.Platform.crossProductExperince.card.analytics.bodyText",
                defaultMessage:
                    "Make faster decisions with accessible data-informed insights."
            }),
            ctaProps: {
                primaryLinkProps: {
                    text: intl.formatMessage({
                        id: "EnterpriseLandingPage.Platform.crossProductExperince.card.linkText",
                        defaultMessage: "Learn more"
                    }),
                    href: useLocalizedHref("/platform/analytics"),
                    type: "primary",
                    dataEventProps: {
                        "data-product-key": "enterprise",
                        "data-event-component": "linkButton",
                        "data-event-container": "platformSection",
                        "data-event": "clicked",
                        "data-label": intl.formatMessage({
                            id: "EnterpriseLandingPage.Platform.crossProductExperince.card.linkText",
                            defaultMessage: "Learn more"
                        }),
                        "data-label-english": "Learn more"
                    }
                }
            }
        },
        {
            headerText: intl.formatMessage({
                id: "EnterpriseLandingPage.Platform.crossProductExperince.card.extensibility.headerText",
                defaultMessage: "Extensibility"
            }),
            bodyText: intl.formatMessage({
                id: "EnterpriseLandingPage.Platform.crossProductExperince.card.extensibility.bodyText",
                defaultMessage:
                    "Customize your tools to meet the unique needs of each team."
            }),
            ctaProps: {
                primaryLinkProps: {
                    text: intl.formatMessage({
                        id: "EnterpriseLandingPage.Platform.crossProductExperince.card.linkText",
                        defaultMessage: "Learn more"
                    }),
                    href: "https://marketplace.atlassian.com/",
                    type: "primary",
                    dataEventProps: {
                        "data-product-key": "enterprise",
                        "data-event-component": "linkButton",
                        "data-event-container": "platformSection",
                        "data-event": "clicked",
                        "data-label": intl.formatMessage({
                            id: "EnterpriseLandingPage.Platform.crossProductExperince.card.linkText",
                            defaultMessage: "Learn more"
                        }),
                        "data-label-english": "Learn more"
                    }
                }
            }
        }
    ];

    const platformCardsEnterpriseGradeCapabilities: CardProps[] = [
        {
            headerText: intl.formatMessage({
                id: "EnterpriseLandingPage.Platform.enterpriseGradeCapabilities.card.resilience.headerText",
                defaultMessage: "Resilience at scale"
            }),
            bodyText: intl.formatMessage({
                id: "EnterpriseLandingPage.Platform.enterpriseGradeCapabilities.card.resilience.bodyText",
                defaultMessage:
                    "Adapt to any challenge with software designed to dynamically scale to meet the needs of any team, regardless of size or complexity."
            }),
            ctaProps: {
                primaryLinkProps: {
                    text: intl.formatMessage({
                        id: "EnterpriseLandingPage.Platform.enterpriseGradeCapabilities.card.linkText",
                        defaultMessage: "Learn more"
                    }),
                    href: useLocalizedHref("/trust/resilience"),
                    type: "primary",
                    dataEventProps: {
                        "data-product-key": "enterprise",
                        "data-event-component": "linkButton",
                        "data-event-container": "platformSection",
                        "data-event": "clicked",
                        "data-label": intl.formatMessage({
                            id: "EnterpriseLandingPage.Platform.enterpriseGradeCapabilities.card.linkText",
                            defaultMessage: "Learn more"
                        }),
                        "data-label-english": "Learn more"
                    }
                }
            }
        },
        {
            headerText: intl.formatMessage({
                id: "EnterpriseLandingPage.Platform.enterpriseGradeCapabilities.card.security.headerText",
                defaultMessage: "Security and compliance"
            }),
            bodyText: intl.formatMessage({
                id: "EnterpriseLandingPage.Platform.enterpriseGradeCapabilities.card.security.bodyText",
                defaultMessage:
                    "Meet regulatory requirements and effectively manage your organization's data using comprehensive controls."
            }),
            ctaProps: {
                primaryLinkProps: {
                    text: intl.formatMessage({
                        id: "EnterpriseLandingPage.Platform.enterpriseGradeCapabilities.card.linkText",
                        defaultMessage: "Learn more"
                    }),
                    href: useLocalizedHref("/trust/data-protection"),
                    type: "primary",
                    dataEventProps: {
                        "data-product-key": "enterprise",
                        "data-event-component": "linkButton",
                        "data-event-container": "platformSection",
                        "data-event": "clicked",
                        "data-label": intl.formatMessage({
                            id: "EnterpriseLandingPage.Platform.enterpriseGradeCapabilities.card.linkText",
                            defaultMessage: "Learn more"
                        }),
                        "data-label-english": "Learn more"
                    }
                }
            }
        }
    ];

    return (
        <UFOSegment name="enterprise-page-platformSection">
            <section css={platformSectionBoxContainer}>
                <Row layout="12">
                    <div css={platformSectionHeaderContainerStyle}>
                        <h2>
                            {intl.formatMessage({
                                id: "EnterpriseLandingPage.Platform.headerText",
                                defaultMessage:
                                    "Unify your teams on one powerful platform"
                            })}
                        </h2>
                    </div>
                </Row>
                <Row layout="12">
                    <div>
                        <div css={platformSectionBoxRowOneStyle}>
                            <h3>
                                {intl.formatMessage({
                                    id: "EnterpriseLandingPage.Platform.crossProductExperince.headerText",
                                    defaultMessage:
                                        "Experiences built to help work flow"
                                })}
                            </h3>
                            <div css={platformSectionRowOneCardContainerStyle}>
                                {platformCardsCrossProductExperience.map(
                                    (cardProps, index) => (
                                        <Card
                                            key={index}
                                            className="platform-row-one-card"
                                            {...cardProps}
                                        />
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                </Row>
                <div css={platformBoxContainer}>
                    <Row layout="12">
                        <div css={platformSectionBoxRowTwoStyle}>
                            <h3>
                                {intl.formatMessage({
                                    id: "EnterpriseLandingPage.Platform.commonDataModel.headerText",
                                    defaultMessage:
                                        "A common data model designed to unlock key insights"
                                })}
                            </h3>
                            <p>
                                {intl.formatMessage({
                                    id: "EnterpriseLandingPage.Platform.commonDataModel.bodyText",
                                    defaultMessage:
                                        "Unify Atlassian and third-party data to connect teams, deepen collaboration, and drive results."
                                })}
                            </p>
                            <Link
                                type="primary"
                                href={useLocalizedHref("/platform")}
                                text={intl.formatMessage({
                                    id: "EnterpriseLandingPage.Platform.commonDataModel.linkText",
                                    defaultMessage: "Learn about our platform"
                                })}
                                dataEventProps={{
                                    "data-product-key": "enterprise",
                                    "data-event-component": "linkButton",
                                    "data-event-container": "platformSection",
                                    "data-event": "clicked",
                                    "data-label": intl.formatMessage({
                                        id: "EnterpriseLandingPage.Platform.commonDataModel.dataLabel",
                                        defaultMessage:
                                            "Learn about our platform"
                                    }),
                                    "data-label-english":
                                        "Learn about our platform"
                                }}
                            />
                        </div>
                    </Row>
                </div>
                <div css={platformBoxContainer}>
                    <Row layout="12">
                        <div css={platformSectionBoxRowThreeStyle}>
                            <h3>
                                {intl.formatMessage({
                                    id: "EnterpriseLandingPage.Platform.enterpriseGradeCapabilites.headerText",
                                    defaultMessage:
                                        "All built on enterprise-grade foundations"
                                })}
                            </h3>
                            <div
                                css={platformSectionRowThreeCardContainerStyle}
                            >
                                {platformCardsEnterpriseGradeCapabilities.map(
                                    (cardProps, index) => {
                                        return (
                                            <Card
                                                key={index}
                                                className="platform-row-three-card"
                                                {...cardProps}
                                            />
                                        );
                                    }
                                )}
                            </div>
                        </div>
                    </Row>
                </div>
            </section>
        </UFOSegment>
    );
};

const platformSectionBoxContainer = css({
    "padding": "40px 0",
    [media.only.sm]: {
        // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".row-layout": {
            paddingInlineStart: "32px",
            paddingInlineEnd: "32px"
        }
    },
    [media.below.sm]: {
        // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".row-layout": {
            paddingInlineStart: "0",
            paddingInlineEnd: "0"
        }
    },
    // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".row-layout": {
        "padding-left": "32px",
        "padding-right": "32px"
    }
});

const platformBoxContainer = css({
    // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".row-layout": {
        margin: "0 auto",
        marginBlockStart: "0"
    }
});

const platformSectionHeaderContainerStyle = css({
    textAlign: "center",
    // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    p: {
        fontSize: "20px",
        maxWidth: "767px",
        margin: "8px auto"
    }
});

const platformSectionBoxRowOneStyle = css({
    background: "#E9F2FE",
    padding: "32px",
    textAlign: "center",
    // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    h3: {
        margin: 0
    }
});

const platformSectionRowOneCardContainerStyle = css({
    "margin": "32px auto",
    "width": "100%",
    "height": "100%",
    "display": "flex",
    "justifyContent": "center",
    "flexWrap": "nowrap",
    "gap": "32px",
    // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".platform-row-one-card": {
        "borderRadius": "16px",
        "flexBasis": "17%",
        "height": "auto",
        "boxShadow": "none",
        // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".text-section": {
            "height": "100%",
            "padding": "16px",
            // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            "h3": {
                fontSize: "16px",
                margin: "0",
                fontWeight: "700"
            },
            // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            "p": {
                marginBlockEnd: "8px",
                height: "100%"
            },
            // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            ".contentBlock-linkFlex": {
                margin: "0",
                // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                a: {
                    fontSize: "1rem",
                    lineHeight: "1.5rem"
                }
            }
        }
    },
    "@media (max-width: 900px)": {
        "flexWrap": "wrap",
        "gap": "24px",
        // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".platform-row-one-card": {
            flexBasis: "29%"
        }
    },
    "@media (max-width: 600px)": {
        // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".platform-row-one-card": {
            flexBasis: "100%"
        }
    }
});

const platformSectionBoxRowTwoStyle = css({
    background: "#CFE1FD",
    padding: "32px",
    textAlign: "center",
    // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    h3: {
        margin: 0
    },
    // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    p: {
        margin: "16px auto",
        maxWidth: "767px",
        width: "80%"
    },
    // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    a: {
        fontSize: "1rem",
        lineHeight: "1.5rem",
        fontWeight: 500
    }
});

const platformSectionBoxRowThreeStyle = css({
    background: "#8FB8F6",
    padding: "32px",
    textAlign: "center",
    // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    h3: {
        margin: 0
    }
});

const platformSectionRowThreeCardContainerStyle = css({
    "margin": "32px auto",
    "width": "80%",
    "display": "flex",
    "justifyContent": "center",
    "flexWrap": "nowrap",
    "gap": "32px",
    // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".platform-row-three-card": {
        "borderRadius": "16px",
        "flexBasis": "50%",
        "height": "auto",
        "boxShadow": "none",
        // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".text-section": {
            "height": "100%",
            "padding": "16px",
            // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            "h3": {
                fontSize: "16px",
                margin: "0",
                fontWeight: "700"
            },
            // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            "p": {
                marginBlockEnd: "8px",
                height: "100%"
            },
            // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            ".contentBlock-linkFlex": {
                margin: "0",
                // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                a: {
                    fontSize: "1rem",
                    lineHeight: "1.5rem"
                }
            }
        }
    },
    "@media (max-width: 600px)": {
        "width": "100%",
        "flexWrap": "wrap",
        "margin": "32px 0",
        // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".platform-row-three-card": {
            flexBasis: "100%"
        }
    }
});
